import React, { useEffect, useState } from 'react';

import { useDecision } from '@optimizely/react-sdk';

const DECISION_NAMES = {
  sort: 'ecomm-sort',
  productRecommendations: 'member_product_recs',
  featuredUI: 'featured-ui',
  similarProducts: 'product-accordion-recs',
  hotjar: 'hotjar',
  flashSaleRedesign: 'flash-sale-redesign',
  shopBanner: 'shop-banner',
};

export const FEATURED_UI_DECISIONS = {
  carousel: 'carousel',
  pinnedProducts: 'pinned_products',
  control: 'control',
  trendingProducts: 'trending_products',
};

export const SIMILAR_PRODUCTS_DECISIONS = {
  similarRecs: 'similar_recs',
  control: 'control',
};

export const HOTJAR_DECISIONS = {
  off: 'off',
  on: 'on',
};

export const FLASH_SALE_REDESIGN_DECISIONS = {
  redesign: 'redesign',
  control: 'control',
};

export const PDP_REDESIGN_DECISIONS = {
  control: 'control',
  redesign1: 'redesign_1',
};

export const getIsExperimentRunning = (featureFlagData) => {
  const isFeatureFlagEnabled = featureFlagData.enabled;
  const isRunning =
    !!featureFlagData.ruleKey &&
    !/^(default-rollout)+(-\d+)+$/.test(featureFlagData.ruleKey);
  return isFeatureFlagEnabled && isRunning;
};

// passes Optimizely feature flag and decision data as a prop
// it would be preferable to use the Optimizely useDecision hook in our components
// we have to use this HOC in App.js because it is a class component
const withOptimizelyFF = (Component) => {
  const OptimizelyHOC = (props) => {
    // useDecision hook returns data about Optimizely flags and decision
    const [sort, isSortReady] = useDecision(DECISION_NAMES.sort, {
      autoUpdate: true,
    });
    const [productRecommendation, isProductRecommendationReady] = useDecision(
      DECISION_NAMES.productRecommendations,
      {
        autoUpdate: true,
      }
    );
    const [featuredUI, isFeaturedUIReady] = useDecision(
      DECISION_NAMES.featuredUI,
      {
        autoUpdate: true,
      }
    );
    const [similarProducts, isSimilarProductsReady] = useDecision(
      DECISION_NAMES.similarProducts,
      {
        autoUpdate: true,
      }
    );
    const [hotjar, isHotjarReady] = useDecision(DECISION_NAMES.hotjar, {
      autoUpdate: true,
    });
    const [flashSaleRedesign, isFlashSaleRedesignReady] = useDecision(
      DECISION_NAMES.flashSaleRedesign,
      {
        autoUpdate: true,
      }
    );
    const [shopBanner, isShopBannerReady] = useDecision(
      DECISION_NAMES.shopBanner,
      {
        autoUpdate: true,
      }
    );
    const [sortData, setSortData] = useState({});
    const [productRecommendationData, setProductRecommendationData] = useState(
      {}
    );
    const [featuredUIData, setFeaturedUIData] = useState({});
    const [similarProductsData, setSimilarProductsData] = useState({});
    const [hotjarData, setHotjarData] = useState({});
    const [flashSaleRedesignData, setFlashSaleRedesignData] = useState({});
    const [shopBannerData, setShopBannerData] = useState({});

    useEffect(() => {
      if (isSortReady) {
        setSortData({
          sortType: sort.enabled ? sort.variables.sort_type : null,
          experimentName: sort.ruleKey,
          variationName: sort.variationKey,
          featureFlagName: 'sort',
          experimentRunning: getIsExperimentRunning(sort),
        });
      }
    }, [isSortReady]);

    useEffect(() => {
      if (isProductRecommendationReady) {
        setProductRecommendationData({
          productRecommendation: productRecommendation.enabled
            ? productRecommendation.variables.recommendation_type
            : null,
          experimentName: productRecommendation.ruleKey,
          variationName: productRecommendation.variationKey,
          featureFlagName: 'productRecommendation',
          experimentRunning: getIsExperimentRunning(productRecommendation),
        });
      }
    }, [isProductRecommendationReady]);

    useEffect(() => {
      if (isFeaturedUIReady) {
        setFeaturedUIData({
          enabled: featuredUI.enabled,
          variationName: featuredUI.variationKey,
          experimentName: featuredUI.ruleKey,
          featureFlagName: featuredUI.flagKey,
          experimentRunning: getIsExperimentRunning(featuredUI),
        });
      }
    }, [isFeaturedUIReady]);

    useEffect(() => {
      if (isSimilarProductsReady) {
        setSimilarProductsData({
          enabled: similarProducts.enabled,
          experimentName: similarProducts.ruleKey,
          variationName: similarProducts.variationKey,
          featureFlagName: similarProducts.flagKey,
          experimentRunning: getIsExperimentRunning(similarProducts),
        });
      }
    }, [isSimilarProductsReady]);

    useEffect(() => {
      if (isHotjarReady) {
        setHotjarData({
          enabled: hotjar.enabled,
          experimentName: hotjar.ruleKey,
          variationName: hotjar.variationKey,
          featureFlagName: hotjar.flagKey,
          experimentRunning: getIsExperimentRunning(hotjar),
        });
      }
    }, [isHotjarReady]);

    useEffect(() => {
      if (isFlashSaleRedesignReady) {
        setFlashSaleRedesignData({
          enabled: flashSaleRedesign.enabled,
          experimentName: flashSaleRedesign.ruleKey,
          variationName: flashSaleRedesign.variationKey,
          featureFlagName: flashSaleRedesign.flagKey,
          experimentRunning: getIsExperimentRunning(flashSaleRedesign),
        });
      }
    }, [isFlashSaleRedesignReady]);

    useEffect(() => {
      if (isShopBannerReady) {
        setShopBannerData({
          variables: shopBanner.variables,
        });
      }
    }, [isShopBannerReady]);

    return (
      <Component
        optimizelySortDecision={sortData}
        optimizelySortReady={isSortReady}
        optimizelyProductRecommendationDecision={productRecommendationData}
        optimizelyProductRecommendationReady={isProductRecommendationReady}
        optimizelyFeaturedUIDecision={featuredUIData}
        optimizelySimilarProducts={similarProductsData}
        optimizelyHotjar={hotjarData}
        optimizelyFlashSaleRedesign={flashSaleRedesignData}
        optimizelyFlashSaleRedesignReady={isFlashSaleRedesignReady}
        optimizelyShopBanner={shopBannerData}
        optimizelyShopBannerReady={isShopBannerReady}
        {...props}
      />
    );
  };

  return OptimizelyHOC;
};

export default withOptimizelyFF;
